/* eslint-disable perfectionist/sort-objects */
import { theme } from '@braskai/ui-kit';

import { USER_PROMPT_FAQ_LINK } from '@/features/project/user-prompting';

import AIScriptImg from '@/shared/assets/images/pricing/featureTooltips/ai-script.jpg';
import APIImg from '@/shared/assets/images/pricing/featureTooltips/api.jpg';
import ApplyChangesForFreeImg from '@/shared/assets/images/pricing/featureTooltips/apply-changes-for-free.jpg';
import CaptionImg from '@/shared/assets/images/pricing/featureTooltips/captions.jpg';
import CustomVoiceCloneImg from '@/shared/assets/images/pricing/featureTooltips/custom-voice-clone.jpg';
import CustomVoiceSettingsImg from '@/shared/assets/images/pricing/featureTooltips/custom-voice-settings.jpg';
import DictionaryImg from '@/shared/assets/images/pricing/featureTooltips/dictionary.jpg';
import HumanInTheLoopImg from '@/shared/assets/images/pricing/featureTooltips/human-in-the-loop.jpg';
import LanguagesImg from '@/shared/assets/images/pricing/featureTooltips/languages.jpg';
import LipSyncImg from '@/shared/assets/images/pricing/featureTooltips/lip-sync.jpg';
import ExtendedMinutesImg from '@/shared/assets/images/pricing/featureTooltips/minutes-extended.jpg';
import MinutesImg from '@/shared/assets/images/pricing/featureTooltips/minutes.jpg';
import SimultaneousMultiLanguageImg from '@/shared/assets/images/pricing/featureTooltips/simultaneous-multi-language.jpg';
import SRTDownloadImg from '@/shared/assets/images/pricing/featureTooltips/srt-download.jpg';
import SRTUploadImg from '@/shared/assets/images/pricing/featureTooltips/srt-upload.jpg';
import TeamspacesImg from '@/shared/assets/images/pricing/featureTooltips/teamspaces.jpg';
import UserPromptingImg from '@/shared/assets/images/pricing/featureTooltips/translation-prompting.png';
import VideoEditorImg from '@/shared/assets/images/pricing/featureTooltips/video-editor.jpg';
import VoiceCloneImg from '@/shared/assets/images/pricing/featureTooltips/voice-clone.jpg';
import {
  AI_REWRITER_DOC_LINK,
  API_DOCUMENTATION_LINK,
  INVOICES_DOC_LINK,
  LANGUAGES_DOC_LINK,
  LIP_SYNC_DOC_LINK,
  MULTILANGUAGE_DOC_LINK,
  RASK_CHAT_LINK,
  SRT_DOWNLOAD_DOC_LINK,
  SRT_UPLOAD_DOC_LINK,
  TEAMSPACES_DOC_LINK,
  TERMS_OF_SERVICE_LINK,
  VIDEO_CAPTIONS_DOC_LINK,
  VOICE_CLONING_DOC_LINK,
} from '@/shared/constants/docLinks';
import { inIframe } from '@/shared/lib/browser';
import { Plan, PlanCategory } from '@/shared/types/Billing';

import { TFeature, TItem } from './types';

export const CATEGORY_ORDER: Record<PlanCategory, number> = {
  business: 4,
  creator: 2,
  creator_pro: 3,
  enterprise: 5,
  starter: 1,
};

export const categoryWithSlider = new Set([PlanCategory.Business, PlanCategory.Creator, PlanCategory.CreatorPro]);

const baseMinutesText = 'Use minutes for Translation';
const advancedMinutesText = 'Use minutes for Translation and Lip-sync';

export const MINUTES_TEXT = {
  business: advancedMinutesText,
  creator: baseMinutesText,
  creator_pro: advancedMinutesText,
  enterprise: advancedMinutesText,
  starter: null,
} as const;

const BASE_TOOLTIP_MINUTES_INFO = {
  title: 'What does minute mean?',
  description:
    '1 minute = 1 minute of translated content. For example, to translate a 1-minute video into two languages, you will need 2 minutes.',
  image: MinutesImg,
};

const EXTENDED_TOOLTIP_MINUTES_INFO = {
  ...BASE_TOOLTIP_MINUTES_INFO,
  description:
    '1 minute = 1 minute of translated content or lip-sync. For example, to translate a 1-minute video into two languages, you will need 2 minutes. To apply lip-sync to these videos, you need 2 more minutes.',
  image: ExtendedMinutesImg,
};

export const TOOLTIP_MINUTES_INFO = {
  business: EXTENDED_TOOLTIP_MINUTES_INFO,
  creator: BASE_TOOLTIP_MINUTES_INFO,
  creator_pro: EXTENDED_TOOLTIP_MINUTES_INFO,
  enterprise: EXTENDED_TOOLTIP_MINUTES_INFO,
  starter: null,
} as const;

export const FEATURE_BLOCKS_KEYS = {
  aiVideoGeneration: 'aiVideoGeneration',
  translation: 'translation',
} as const;

export const ENTERPRISE_BACKGROUND_COLOR = '#1E1E70'; //color used only in pricing page
export const BUSINESS_BACKGROUND_COLOR = '#E7E7FC'; //color used only in pricing page

export const PRICING_PLAN_CARD_NOTES = {
  [PlanCategory.Business]: {
    label: 'Best Value',
    backgroundColor: BUSINESS_BACKGROUND_COLOR,
    color: 'grey.700',
  },
  [PlanCategory.Creator]: {
    label: 'Best for individuals',
    backgroundColor: theme.palette.common.white,
    color: 'grey.500',
  },
  [PlanCategory.CreatorPro]: {
    label: 'Most Popular',
    backgroundColor: theme.palette.primary[600],
    color: 'common.white',
  },
  [PlanCategory.Enterprise]: {
    label: 'Tailored onboarding & SLA',
    backgroundColor: ENTERPRISE_BACKGROUND_COLOR,
    color: 'common.white',
  },
  [PlanCategory.Starter]: {
    label: 'Best for beginners',
    backgroundColor: theme.palette.common.white,
    color: 'grey.500',
  },
} as const;

export const FEATURES_KEYS = {
  aiRewriter: 'aiRewriter',
  allBusiness: 'allBusiness',
  allCreator: 'allCreator',
  allCreatorPro: 'allCreatorPro',
  api: 'api',
  applyChangesForFree: 'applyChangesForFree',
  businessContract: 'businessContract',
  customerSuccessManager: 'customerSuccessManager',
  customVoiceClone: 'customVoiceClone',
  customVoiceCloneBusiness: 'customVoiceCloneBusiness',
  customVoiceCloneEnterprise: 'customVoiceCloneEnterprise',
  customVoiceClonePro: 'customVoiceClonePro',
  customVoiceSettings: 'customVoiceSettings',
  flexibleMinutes: 'flexibleMinutes',
  humanInTheLoop: 'humanInTheLoop',
  lipSync: 'lipSync',
  machineTranslation: 'machineTranslation',
  multiLanguage: 'multiLanguage',
  noWatermark: 'noWatermark',
  priorityProcessing: 'priorityProcessing',
  speechToText: 'speechToText',
  srtDownload: 'srtDownload',
  srtDownloadUpload: 'srtDownloadUpload',
  srtUpload: 'srtUpload',
  support247: 'support247',
  teamspaces: 'teamspaces',
  teamspacesBusiness: 'teamspacesBusiness',
  teamspacesCreatorPro: 'teamspacesCreatorPro',
  teamspacesEnterprise: 'teamspacesEnterprise',
  translate: 'translate',
  translationDictionary: 'translationDictionary',
  userPrompting: 'userPrompting',
  videoCaptions: 'videoCaptions',
  videoEditor: 'videoEditor',
  voiceCloning: 'voiceCloning',
  watermark: 'watermark',
  onlyVideoDownload: 'onlyVideoDownload',
} as const;

export const FEATURES_MAP: Record<keyof typeof FEATURES_KEYS, TFeature> = {
  [FEATURES_KEYS.aiRewriter]: {
    description: 'AI script adjustment',
    tooltip: {
      title: 'Improve translated transcript with AI-driven alignment for optimal speech synchronisation',
      image: AIScriptImg,
      link: AI_REWRITER_DOC_LINK,
    },
  },
  [FEATURES_KEYS.allBusiness]: {
    description: 'All Business features, plus:',
  },
  [FEATURES_KEYS.allCreator]: {
    description: 'All Creator features, plus:',
  },
  [FEATURES_KEYS.allCreatorPro]: {
    description: 'All Creator Pro features, plus:',
  },
  [FEATURES_KEYS.api]: {
    description: 'API',
    tooltip: {
      title: 'Seamlessly integrate Rask with your existing platforms and workflows',
      image: APIImg,
      link: API_DOCUMENTATION_LINK,
      linkTitle: 'Read documentation',
    },
  },
  [FEATURES_KEYS.applyChangesForFree]: {
    description: 'Apply changes for free',
    tooltip: {
      title: 'Change transcript, timestamps and voice settings free of charge',
      image: ApplyChangesForFreeImg,
    },
  },
  [FEATURES_KEYS.businessContract]: {
    description: 'Business contract and invoicing',
  },
  [FEATURES_KEYS.customerSuccessManager]: {
    description: 'Customer Success Manager',
  },
  [FEATURES_KEYS.customVoiceClone]: {
    description: 'Voice Clone library',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceCloneBusiness]: {
    description: '10 custom voice clones',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceCloneEnterprise]: {
    description: 'Custom',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceClonePro]: {
    description: '3 custom voice clones',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceSettings]: {
    description: 'Voice Clone settings',
    tooltip: {
      title: 'Adjust the level of similarity and expressiveness of your voice clone.',
      description: 'Create diverse voice tones to have full control over the result.',
      image: CustomVoiceSettingsImg,
    },
  },
  [FEATURES_KEYS.flexibleMinutes]: {
    description: 'Flexible minutes pricing',
  },
  [FEATURES_KEYS.humanInTheLoop]: {
    description: 'Human-in-the-loop',
    tooltip: {
      title: 'Enhance AI dubbing results',
      description: 'Request human proofreading for translated speech to create top-quality content',
      image: HumanInTheLoopImg,
    },
  },
  [FEATURES_KEYS.lipSync]: {
    description: 'Multispeaker Lip-sync',
    tooltip: {
      title: 'Get natural and top-quality dubbing by syncing mouth movements with translated audio.',
      description:
        'It takes 1 extra minute to apply lip-sync to 1 minute of translated video. You can try 1 free minute to test the quality.',
      image: LipSyncImg,
      link: LIP_SYNC_DOC_LINK,
    },
  },
  [FEATURES_KEYS.machineTranslation]: {
    description: 'Automated machine translation',
  },
  [FEATURES_KEYS.multiLanguage]: {
    description: 'Simultaneous multi-language translation',
    tooltip: {
      title: 'Create multi-language projects and instantly translate your content into several languages at once',
      image: SimultaneousMultiLanguageImg,
      link: MULTILANGUAGE_DOC_LINK,
    },
  },

  [FEATURES_KEYS.noWatermark]: {
    description: 'No Watermark',
  },
  [FEATURES_KEYS.onlyVideoDownload]: {
    description: 'Only video download',
  },
  [FEATURES_KEYS.priorityProcessing]: {
    description: 'Priority processing',
  },
  [FEATURES_KEYS.speechToText]: {
    description: 'Auto speech-to-text transcription',
  },
  [FEATURES_KEYS.srtDownload]: {
    description: 'SRT download',
    tooltip: {
      title: 'Download .SRT subtitle file containing both translated and original video transcript',
      image: SRTDownloadImg,
      link: SRT_DOWNLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.srtDownloadUpload]: {
    description: 'SRT download & upload',
    tooltip: {
      title: 'Download subtitles with original and translated text, or upload your own to sync with dubbing.',
      image: SRTDownloadImg,
      link: SRT_DOWNLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.srtUpload]: {
    description: 'SRT upload',
    tooltip: {
      title: 'Upload your own video transcript and effortlessly pair it with video for accurate dubbing results',
      image: SRTUploadImg,
      link: SRT_UPLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.support247]: {
    description: 'Support 24/7',
  },
  [FEATURES_KEYS.teamspaces]: {
    description: 'Teamspaces',
  },
  [FEATURES_KEYS.teamspacesBusiness]: {
    description: '1 Teamspace, 5 members',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.teamspacesCreatorPro]: {
    description: '1 Teamspace, 3 members',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.teamspacesEnterprise]: {
    description: 'Custom',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.translate]: {
    description: 'Translations in 135 languages',
    tooltip: {
      title: 'Translate your content into 135 languages and dialects',
      description: 'Depends on the language you can use AI voices or Voice Clone feature',
      image: LanguagesImg,
      link: LANGUAGES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.translationDictionary]: {
    description: 'Translation dictionary',
    tooltip: {
      title: 'Save your custom terminology to ensure consistent automatic translation across all projects',
      image: DictionaryImg,
    },
  },
  [FEATURES_KEYS.userPrompting]: {
    description: 'Translation prompting',
    tooltip: {
      description:
        'Set the tone, style, and context for translations using prompts. Use our templates or create your own to match your specific needs.',
      image: UserPromptingImg,
      link: USER_PROMPT_FAQ_LINK,
    },
  },
  [FEATURES_KEYS.videoCaptions]: {
    description: 'Automated captions/subtitles',
    tooltip: {
      title: 'Generate subtitles and embed them into your translated video',
      image: CaptionImg,
      link: VIDEO_CAPTIONS_DOC_LINK,
    },
  },
  [FEATURES_KEYS.videoEditor]: {
    description: 'Advanced video editor',
    tooltip: {
      title:
        'Easily edit AI-generated content for translation, transcription, timestamps, speakers, and voices to achieve top-quality dubbing results!',
      image: VideoEditorImg,
    },
  },
  [FEATURES_KEYS.voiceCloning]: {
    description: 'Voice Cloning in 32 languages',
    tooltip: {
      title: 'Voice Cloning in 32 languages',
      description:
        'English, Japanese, Chinese, German, Hindi, French, Korean, Portuguese, Italian, Spanish, Indonesian, Dutch, Turkish, Filipino, Polish, Swedish, Bulgarian, Romanian, Arabic, Czech, Greek, Finnish, Croatian, Malay, Slovak, Danish, Tamil, Ukrainian, Russian, Hungarian, Norwegian, Vietnamese.',
      image: VoiceCloneImg,
      link: VOICE_CLONING_DOC_LINK,
    },
  },
  [FEATURES_KEYS.watermark]: {
    description: 'Watermark',
  },
};

// Translation features list
const starterTranslationFeaturesList = [
  FEATURES_KEYS.translate,
  FEATURES_KEYS.machineTranslation,
  FEATURES_KEYS.speechToText,
  FEATURES_KEYS.voiceCloning,
];
const creatorTranslationFeaturesList = [
  FEATURES_KEYS.translate,
  FEATURES_KEYS.speechToText,
  FEATURES_KEYS.machineTranslation,
  FEATURES_KEYS.voiceCloning,
  FEATURES_KEYS.videoEditor,
  FEATURES_KEYS.applyChangesForFree,
  ...(inIframe() ? [] : [FEATURES_KEYS.api]),
  FEATURES_KEYS.noWatermark,
];

const creatorProTranslationFeaturesList = [
  FEATURES_KEYS.allCreator,
  FEATURES_KEYS.lipSync,
  FEATURES_KEYS.srtDownload,
  FEATURES_KEYS.srtUpload,
  FEATURES_KEYS.aiRewriter,
  FEATURES_KEYS.videoCaptions,
  FEATURES_KEYS.customVoiceSettings,
  FEATURES_KEYS.customVoiceClonePro,
  FEATURES_KEYS.teamspacesCreatorPro,
];

const businessTranslationFeaturesList = [
  FEATURES_KEYS.allCreatorPro,
  FEATURES_KEYS.multiLanguage,
  FEATURES_KEYS.translationDictionary,
  FEATURES_KEYS.customVoiceCloneBusiness,
  FEATURES_KEYS.teamspacesBusiness,
];

const enterpriseTranslationFeaturesList = [
  FEATURES_KEYS.allBusiness,
  FEATURES_KEYS.humanInTheLoop,
  FEATURES_KEYS.customerSuccessManager,
  FEATURES_KEYS.flexibleMinutes,
  FEATURES_KEYS.businessContract,
  FEATURES_KEYS.customVoiceCloneEnterprise,
  FEATURES_KEYS.teamspacesEnterprise,
  FEATURES_KEYS.userPrompting,
];

export const featuresByCategory = {
  [PlanCategory.Business]: businessTranslationFeaturesList,
  [PlanCategory.Creator]: creatorTranslationFeaturesList,
  [PlanCategory.CreatorPro]: creatorProTranslationFeaturesList,
  [PlanCategory.Enterprise]: enterpriseTranslationFeaturesList,
  [PlanCategory.Starter]: starterTranslationFeaturesList,
};

// FAQ items

export const FAQ_ITEMS: TItem[] = [
  {
    answer: `1 minute is a universal credit you can spend on Translation or Lip-sync.

    Translation: 1 minute equals 1 minute of final translated video/audio. For example, if you have a 5-minute video that needs to be translated into 2 new languages, you will need 10 minutes.

    Lip-sync: 1 minute of lip-synced video equals 1 additional minute. So, to translate and lip-sync a 1-minute video into 1 language, you need 2 minutes.`,
    question: `What does one minute mean?`,
  },

  {
    answer: `Depending on how many minutes you need, you can choose one of the pricing options ranging from 25 to 1000 minutes per month. You can upgrade or downgrade your plan anytime if next month you need more or fewer minutes than before.

    Here are all the available plans:

    Creator Plan
    25 minutes ($60/month - monthly billing, $50/month - yearly billing)
    50 minutes ($100/month - monthly billing, $80/month - yearly billing)

    Creator Pro Plan
    100 minutes ($150/month - monthly billing, $120/month - yearly billing)
    200 minutes ($300/month - monthly billing, $240/month - yearly billing)
    300 minutes ($450/month - monthly billing, $360/month - yearly billing)

    Business Plan
    500 minutes ($750/month - monthly billing, $600/month - yearly billing)
    750 minutes ($1125/month - monthly billing, $900/month - yearly billing)
    1000 minutes ($1500/month - monthly billing, $1200/month - yearly billing)
    2000 minutes ($3000/month - monthly billing, $2400/month - yearly billing)

    The opportunity to purchase additional minutes is available on the Business plan for $3 each.`,
    question: `How to choose the right pricing plan?`,
  },

  {
    answer: `Our regular pricing plans provide up to 2000 minutes per month. If you need more minutes, you can purchase them for $3 per minute on a Business plan. We can create a custom plan for you with Enterprise features if you require a larger volume of minutes regularly. To discuss your needs and explore these options, book a call with our partnership specialist using this link: [${RASK_CHAT_LINK}]`,
    isParsingRequire: true,
    question: `What should I do if 2000 minutes per month are insufficient for my needs?`,
  },

  {
    answer: `Yes, you can. We will save the remaining minutes in your account; you can use them as long as your subscription is active.

    In case of subscription cancellation, all remaining minutes will be automatically removed, and your access to the service will be frozen.

    To keep your remaining minutes, pause your subscription instead of cancellation.`,
    question: `Can I use the remaining minutes from the current month of my subscription in the following months?`,
  },

  {
    answer: `We offer two types of subscriptions: monthly and annual. The monthly subscription involves automatic recurring payments that renew every month. The annual subscription is a recurring payment billed annually and automatically renews yearly. After subscribing, you will be charged until you cancel your subscription. You can cancel your subscription anytime`,
    question: `Is it a one-time payment or a recurring payment?`,
  },

  {
    answer: `Yes, of course. To upgrade your subscription, click on your account in the bottom left corner and then click on “Upgrade”. This will allow you to change your current plan and select a different subscription tier that better aligns with your needs.

    If you have any questions about upgrading or downgrading your subscription, please contact us via the support chat.`,
    question: `Can I change my plan?`,
  },

  {
    answer: `Yes, you can cancel your subscription anytime. To do so, click on your account in the bottom left corner and then click on “Cancel subscription”.

    In the event of subscription cancellation, any remaining minutes will be automatically removed at the end of the billing cycle, and your access to the service will be suspended.

    To retain your remaining minutes, consider downgrading or pausing your subscription instead of canceling.`,
    question: `Can I cancel my subscription anytime?`,
  },

  {
    answer: `We are committed to refunding the minutes you have used if the creation of your video failed to complete due to a technical error on our end. To request a refund of minutes, please get in touch with our support service. We will review your request, and if it is confirmed that there was a technical error on our end, we will return the used minutes to your account.

    Please note that refunds are not provided in the following cases:
    • More than 25% of the subscription plan or more than 25 minutes has been used
    • More than 14 days have passed since the activation of the subscription.
    • The client received an expected, technically high-quality result but simply changed their mind about using the service.

    Additional Refund Condition for Subscription Renewals: When renewing a subscription, you can request a refund within 24 hours, provided that no minutes are used for the new month. This applies specifically to renewed subscriptions and is contingent upon the account remaining inactive in terms of minute usage following the renewal.

    Each refund request will be considered individually. You can learn more about our refund policy in our terms of service by following (this link)[${TERMS_OF_SERVICE_LINK}]`,
    isParsingRequire: true,
    question: `What is your refund policy?`,
  },

  {
    answer: `We support various payment methods, including credit/debit cards (Visa, Mastercard, American Express, UnionPay, and others) and payments through Apple Pay, Cash App Pay, Google Pay, Link, WeChat Pay. All payments are securely processed through Stripe, a trusted payment platform.`,
    question: `What payment methods do you accept?`,
  },

  {
    answer: `To include information about your company in the invoice, click on "Company Information" and fill in all the fields when purchasing the subscription.

    The invoices are automatically sent to your email after the payment is processed. You can learn how to make adjustments to your future invoices by following (this link)[${INVOICES_DOC_LINK}].

    If you need to make changes to invoices created in the past, please contact us via the support chat, and we will assist you with the necessary modifications.`,
    isParsingRequire: true,
    question: `Can I include company information on my invoice?`,
  },

  {
    answer: `We take the security of our customer's payment information very seriously. To protect your payment information, we use Stripe, one of the world's leading payment processors. Stripe is certified as a PCI Level 1 Service Provider, the highest security certification in the payment industry.

    Stripe encrypts your payment information using industry-standard SSL/TLS technology and stores it on their secure servers. We never store any payment information on our servers.

    By using Stripe, we can ensure that your payment information is always safe and secure.`,
    question: `How do you ensure the security of my payment information?`,
  },

  {
    answer: `With Rask AI, you can translate from nearly any language to over (130 languages)[${LANGUAGES_DOC_LINK}]. Our Voice Cloning feature offers a human-like experience, and it is currently available when dubbing from any source language to the following 29 languages: English, Japanese, Chinese, German, Hindi, French, Korean, Portuguese, Italian, Spanish, Indonesian, Dutch, Turkish, Filipino, Polish, Swedish, Bulgarian, Romanian, Arabic, Czech, Greek, Finnish, Croatian, Malay, Slovak, Danish, Tamil, Ukrainian, Russian.`,
    isParsingRequire: true,
    question: `How many languages do you currently support?`,
  },

  {
    answer: `Currently, you can add subtitles to your video with two options:
    - Generate subtitles directly within our product and embed them into video
    - Download subtitles in SRT format.`,
    question: `Is it possible to add subtitles or captions to the dubbed video?`,
  },

  {
    answer: `For users with subscriptions, there are no specific limitations on the duration and size of uploaded videos. However, if you encounter any issues with videos longer than 3 hours, we recommend dividing them into two parts and translating them separately. This approach ensures smoother processing and helps maintain optimal performance.`,
    question: `Are there any limitations on the duration and size of uploaded videos?`,
  },

  {
    answer: `Support chat is available within the product for users with subscriptions. Alternatively, you can also send any questions to *info@rask.ai*`,
    isParsingRequire: true,
    question: `How can you contact us?`,
  },
];

export const CUSTOM_PLAN_DATA: Plan = {
  id: 'enterprise',
  name: 'Enterprise',
  bundles: null,
  category: PlanCategory.Enterprise,
  description: 'For enterprises seeking tailored service',
  is_archived: false,
  metadata: {
    label: null,
    bundle_position: 1,
    bundle_value: 'From 2000 min/month',
    bundle_value_default: false,
    extra_minutes_price: 'Custom price',
    highlighted_button: null,
    position: 4,
  },
  minutes_to_add: null,
  prices: [
    { id: 'price_1NC0kzEQhzwkdhyH81hM4KtE', currency: 'usd', recurring: null, status: 'active', unit_amount: 0 },
  ],
};
export const CUSTOM_PLAN_DATA_NEW = {
  name: 'Enterprise',
  category: PlanCategory.Enterprise,
  description: 'For enterprises seeking tailored service',
  plans: [CUSTOM_PLAN_DATA],
};

//eslint-disable-
export const FEATURES_PER_PLAN_CATEGORY = {
  creditsAndGenerations: {
    [FEATURES_KEYS.api]: [
      {
        enabled: true,
      },
      {
        enabled: true,
      },
      {
        enabled: true,
      },
      {
        enabled: true,
      },
    ],
    [FEATURES_KEYS.speechToText]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.translate]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.voiceCloning]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.lipSync]: [{ enabled: false }, { enabled: true }, { enabled: true }, { enabled: true }],
  },
  editor: {
    [FEATURES_KEYS.videoEditor]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.applyChangesForFree]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.aiRewriter]: [{ enabled: false }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.srtDownloadUpload]: [{ enabled: false }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.videoCaptions]: [{ enabled: false }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.customVoiceSettings]: [{ enabled: false }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.customVoiceClone]: [
      { enabled: false },
      { customFeatureName: FEATURES_KEYS.customVoiceClonePro, enabled: true },
      { customFeatureName: FEATURES_KEYS.customVoiceCloneBusiness, enabled: true },
      { customFeatureName: FEATURES_KEYS.customVoiceCloneEnterprise, enabled: true },
    ],
    [FEATURES_KEYS.multiLanguage]: [{ enabled: false }, { enabled: false }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.translationDictionary]: [
      { enabled: false },
      { enabled: false },
      { enabled: true },
      { enabled: true },
    ],
    [FEATURES_KEYS.userPrompting]: [{ enabled: false }, { enabled: false }, { enabled: false }, { enabled: true }],
  },
  supportAndTeamwork: {
    [FEATURES_KEYS.support247]: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.teamspaces]: [
      { enabled: false },
      { customFeatureName: FEATURES_KEYS.teamspacesCreatorPro, enabled: true },
      { customFeatureName: FEATURES_KEYS.teamspacesBusiness, enabled: true },
      { customFeatureName: FEATURES_KEYS.teamspacesEnterprise, enabled: true },
    ],
    [FEATURES_KEYS.priorityProcessing]: [{ enabled: false }, { enabled: false }, { enabled: true }, { enabled: true }],
    [FEATURES_KEYS.customerSuccessManager]: [
      { enabled: false },
      { enabled: false },
      { enabled: false },
      { enabled: true },
    ],
    [FEATURES_KEYS.humanInTheLoop]: [{ enabled: false }, { enabled: false }, { enabled: false }, { enabled: true }],
    [FEATURES_KEYS.businessContract]: [{ enabled: false }, { enabled: false }, { enabled: false }, { enabled: true }],
  },
};
